import { Platform } from "react-native";
import { VIDEO_SERVER_BASE_URL } from "../config/videoServer";
import { getShareAttribution } from "./shareAttribution";

export type AccessIdentity = {
  uid?: string;
  displayName?: string | null;
  email?: string;
} | null;

export type AccessEventInput = {
  event:
    | "page_view"
    | "page_leave"
    | "video_open"
    | "video_play"
    | "video_pause"
    | "video_seek"
    | "video_end"
    | "video_close"
    | "video_heartbeat"
    | "video_favorite"
    | "video_unfavorite"
    | "video_comment"
    | "share_open"
    | "share_click"
    | "share_result"
    | "share_landing"
    | "search_query";
  page?: string;
  videoId?: string;
  videoTitle?: string;
  durationMs?: number;
  positionSeconds?: number;
  watchSecondsDelta?: number;
  playbackId?: string;
  clientEventId?: string;
  searchQuery?: string;
  shareId?: string;
  shareSource?: string;
  shareMethod?: string;
  shareResult?: string;
};

const randomId = () =>
  globalThis.crypto?.randomUUID?.() ||
  `${Date.now()}-${Math.random().toString(16).slice(2)}`;

function storedId(storage: Storage | undefined, key: string) {
  if (!storage) return randomId();
  const current = storage.getItem(key);
  if (current) return current;
  const created = randomId();
  storage.setItem(key, created);
  return created;
}

const webWindow = Platform.OS === "web" && typeof window !== "undefined" ? window : null;
const cookieValue = (name: string) => {
  if (!webWindow) return "";
  const prefix = `${encodeURIComponent(name)}=`;
  return webWindow.document.cookie
    .split(";")
    .map((item) => item.trim())
    .find((item) => item.startsWith(prefix))
    ?.slice(prefix.length) || "";
};

const storedVisitorId = webWindow?.localStorage?.getItem("neptuneVisitorId") || "";
const decodeCookie = (value: string) => {
  try { return decodeURIComponent(value); } catch { return ""; }
};
const visitorId = decodeCookie(cookieValue("neptune_visitor_id")) || storedVisitorId || randomId();
webWindow?.localStorage?.setItem("neptuneVisitorId", visitorId);
const sessionId = storedId(webWindow?.sessionStorage, "neptuneSessionId");

export const getAccessVisitorId = () => visitorId;
export const getAccessSessionId = () => sessionId;

type VisitorPolicy = { enabled?: boolean; cookieEnabled?: boolean; cookieDays?: number };
const policyPromise: Promise<VisitorPolicy> = webWindow
  ? fetch(`${VIDEO_SERVER_BASE_URL}/api/access-policy`, { credentials: "include" })
      .then((response) => response.ok ? response.json() : {})
      .catch(() => ({}))
  : Promise.resolve({});

const clientHintsPromise = (async () => {
  const nav = webWindow?.navigator as any;
  if (!nav?.userAgentData) return {};
  let highEntropy: Record<string, unknown> = {};
  try {
    highEntropy = await nav.userAgentData.getHighEntropyValues([
      "architecture", "bitness", "model", "platformVersion",
    ]);
  } catch {}
  return {
    platform: nav.userAgentData.platform || "",
    mobile: nav.userAgentData.mobile === true,
    brands: (nav.userAgentData.brands || []).map((item: any) => `${item.brand} ${item.version}`).join(", "),
    ...highEntropy,
  };
})();

function setVisitorCookie(days: number) {
  if (!webWindow) return;
  const secure = webWindow.location.protocol === "https:" ? "; Secure" : "";
  webWindow.document.cookie = `neptune_visitor_id=${encodeURIComponent(visitorId)}; Path=/; Max-Age=${Math.round(days * 86400)}; SameSite=Lax${secure}`;
}

export function recordAccessEvent(input: AccessEventInput, identity: AccessIdentity) {
  return (async () => {
    const policy = await policyPromise;
    if (policy.enabled === false) return;
    if (policy.cookieEnabled !== false) setVisitorCookie(Number(policy.cookieDays || 365));
    const nav = webWindow?.navigator as any;
    const connection = nav?.connection || nav?.mozConnection || nav?.webkitConnection || {};
    const payload = {
      ...input,
      shareAttribution: getShareAttribution(),
      userId: identity?.uid || "",
      // Never send passwords, auth tokens, or arbitrary cookie contents.
      userLabel: identity?.displayName || (identity?.uid ? "signed-in-user" : "anonymous"),
      visitorId,
      cookieVisitorId: policy.cookieEnabled === false ? "" : visitorId,
      cookieEnabled: nav?.cookieEnabled === true,
      sessionId,
      path: webWindow?.location.href || "native-app",
      title: webWindow?.document.title || "Neptune Entertainment",
      referrer: webWindow?.document.referrer || "",
      language: nav?.language || "",
      languages: Array.isArray(nav?.languages) ? nav.languages.join(", ") : "",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
      screen: webWindow ? `${webWindow.screen.width}x${webWindow.screen.height}` : "native",
      viewport: webWindow ? `${webWindow.innerWidth}x${webWindow.innerHeight}` : "native",
      colorDepth: webWindow?.screen.colorDepth || 0,
      pixelRatio: webWindow?.devicePixelRatio || 0,
      orientation: webWindow?.screen.orientation?.type || "",
      clientHints: await clientHintsPromise,
      network: {
        effectiveType: connection.effectiveType || "",
        type: connection.type || "",
        downlink: connection.downlink || 0,
        rtt: connection.rtt || 0,
        saveData: connection.saveData === true,
        online: nav?.onLine !== false,
      },
      hardware: {
        hardwareConcurrency: nav?.hardwareConcurrency || 0,
        deviceMemory: nav?.deviceMemory || 0,
        maxTouchPoints: nav?.maxTouchPoints || 0,
      },
      privacy: {
        doNotTrack: nav?.doNotTrack || "",
        globalPrivacyControl: nav?.globalPrivacyControl === true,
        cookieEnabled: nav?.cookieEnabled === true,
      },
    };

    await fetch(`${VIDEO_SERVER_BASE_URL}/api/access-events`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {});
  })().catch(() => {});
}

export function beginPageVisit(page: string, identity: AccessIdentity) {
  const startedAt = Date.now();
  recordAccessEvent({ event: "page_view", page }, identity);
  return () => {
    void recordAccessEvent(
      { event: "page_leave", page, durationMs: Date.now() - startedAt },
      identity,
    );
  };
}
